import { ForumResponseModel } from "generated/api";
import moment, { Moment } from "moment";

export const DEFAULT_FORUM_DATE_FORMAT = "DD/MM/YYYY";
export const US_FORUM_DATE_FORMAT = "MM/DD/YYYY";
export const FORUM_TIME_FORMAT = "HH:mm";
export const DEFAULT_FORUM_DATE_TIME_FORMAT = `${DEFAULT_FORUM_DATE_FORMAT} ${FORUM_TIME_FORMAT}`;
export const US_FORUM_DATE_TIME_FORMAT = `${US_FORUM_DATE_FORMAT} ${FORUM_TIME_FORMAT}`;

type ForumFormatSource = Pick<ForumResponseModel, "countryCode" | "country"> | string | null | undefined;

export const isUnitedStatesForum = (forumOrCountryCode: ForumFormatSource) => {
  if (typeof forumOrCountryCode === "string") {
    return forumOrCountryCode.toUpperCase() === "US" || forumOrCountryCode.toLowerCase() === "united states";
  }

  const countryCode = forumOrCountryCode?.countryCode;
  const country = forumOrCountryCode?.country;

  return countryCode?.toUpperCase() === "US" || country?.toLowerCase() === "united states";
};

export const getForumDateFormat = (forumOrCountryCode: ForumFormatSource) =>
  isUnitedStatesForum(forumOrCountryCode) ? US_FORUM_DATE_FORMAT : DEFAULT_FORUM_DATE_FORMAT;

export const getForumDateTimeFormat = (forumOrCountryCode: ForumFormatSource) =>
  isUnitedStatesForum(forumOrCountryCode) ? US_FORUM_DATE_TIME_FORMAT : DEFAULT_FORUM_DATE_TIME_FORMAT;

export const getForumTimeFormat = () => FORUM_TIME_FORMAT;

export const getForumDateDisplayFormat = (
  forumOrCountryCode: ForumFormatSource,
  { hideDate = false, showTime = false }: { hideDate?: boolean; showTime?: boolean } = {},
) => {
  const dateFormat = hideDate ? "" : getForumDateFormat(forumOrCountryCode);
  const timeFormat = showTime ? getForumTimeFormat() : "";

  return `${dateFormat} ${timeFormat}`.trim();
};

export const hasTimezone = (dateString: string) => {
  if (dateString.at(-1) === "Z") {
    return true;
  }

  const temp = dateString.at(-6);
  return temp === "+" || temp === "-";
};

export const stripTimezone = (dateString: string) => {
  return dateString.replace(/(Z|[+-]\d{2}:\d{2})$/, "");
};

export const toWallClockMoment = (date: object | string | null | undefined) => {
  if (typeof date === "string") {
    return moment(stripTimezone(date));
  }

  return moment(date);
};

export const formatForumDate = (
  date: object | string | null | undefined,
  forumOrCountryCode: ForumFormatSource,
  {
    fallback = "",
    hideDate = false,
    showTime = false,
    wallClock = false,
  }: {
    fallback?: string;
    hideDate?: boolean;
    showTime?: boolean;
    wallClock?: boolean;
  } = {},
) => {
  const value = wallClock ? toWallClockMoment(date) : moment(date);
  const format = getForumDateDisplayFormat(forumOrCountryCode, { hideDate, showTime });

  if (!date || !value.isValid() || !format) {
    return fallback;
  }

  return value.format(format);
};

export const getForumDisplayDateByStartAndEnd = (startDate: Moment, endDate: Moment) => {
  if (!startDate.isValid() || !endDate.isValid()) {
    return null;
  }

  if (!startDate.isSame(endDate, "year")) {
    return `${startDate.format("DD.MM.YY")}-${endDate.format("DD.MM.YY")}`;
  } else if (!startDate.isSame(endDate, "month")) {
    return `${startDate.format("DD.MM")}-${endDate.format("DD.MM.YY")}`;
  } else {
    return `${startDate.format("DD")}-${endDate.format("DD.MM.YY")}`;
  }
};

export const getForumDisplayDateByDuration = (duration: Moment[]) => {
  if (duration && duration.length > 1) {
    const start = duration[0];
    const end = duration[1];

    if (!start.isSame(end, "year")) {
      return `${start.format("DD.MM.YY")}-${end.format("DD.MM.YY")}`;
    } else if (!start.isSame(end, "month")) {
      return `${start.format("DD.MM")}-${end.format("DD.MM.YY")}`;
    } else {
      return `${start.format("DD")}-${end.format("DD.MM.YY")}`;
    }
  }

  return null;
};

export const getDisabledDates = (value: Moment, forum: ForumResponseModel) => {
  const day = toWallClockMoment(value).startOf("day");
  const start = toWallClockMoment(forum?.startDate).startOf("day");
  const end = toWallClockMoment(forum?.endDate).endOf("day");

  return day.isBefore(start) || day.isAfter(end);
};

export const getFormattedRangeDateTime = (
  startDate: string,
  endDate: string,
  forumOrCountryCode?: ForumFormatSource,
) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const dateTimeFormat = getForumDateTimeFormat(forumOrCountryCode);
  const timeFormat = getForumTimeFormat();

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  if (start.isSame(end, "day")) {
    return `${start.format(dateTimeFormat)} - ${end.format(timeFormat)}`;
  } else {
    return `${start.format(dateTimeFormat)} - ${end.format(dateTimeFormat)}`;
  }
};
